import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { MapPin, Loader2, CheckCircle2, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CRMGebiete() {
  const { user } = useAuth();
  const [gebiete, setGebiete] = useState([]);
  const [anfragen, setAnfragen] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${user?.token}` };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [gRes, aRes] = await Promise.all([
        axios.get(`${API}/gebiete`),
        axios.get(`${API}/alltagsbegleitung/anfragen`, { headers }),
      ]);
      setGebiete(gRes.data);
      setAnfragen(aRes.data);
    } catch { toast.error("Fehler beim Laden."); }
    setLoading(false);
  };

  const toggleStatus = async (slug) => {
    try {
      const res = await axios.patch(`${API}/gebiete/${slug}`, {}, { headers });
      setGebiete(prev => prev.map(g => g.slug === slug ? { ...g, status: res.data.status } : g));
      toast.success(`${slug}: ${res.data.status === "warteliste" ? "Warteliste aktiviert" : "Verfügbar gesetzt"}`);
    } catch { toast.error("Fehler."); }
  };

  const getStats = (name) => {
    const gebietAnfragen = anfragen.filter(a => (a.gebiet || a.stadt || "").toLowerCase() === name.toLowerCase());
    return {
      total: gebietAnfragen.length,
      warteliste: gebietAnfragen.filter(a => a.kapazitaetsstatus === "warteliste").length,
      neu: gebietAnfragen.filter(a => a.status === "neu").length,
      fenster: gebietAnfragen.filter(a => a.fensterreinigung_interesse === true).length,
    };
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-cyan-700" /></div>;

  return (
    <div data-testid="crm-gebiete">
      <div className="mb-6">
        <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">Gebiete & Verfügbarkeit</h1>
        <p className="text-sm text-slate-500 mt-1">Kapazitätsstatus für Alltagsbegleitung verwalten</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {gebiete.map(g => {
          const stats = getStats(g.name);
          const isWarte = g.status === "warteliste";
          if (g.status === "inaktiv") return <div key={g.slug} className="rounded-xl border border-dashed border-slate-300 p-3 text-sm text-slate-500" data-testid={`gebiet-inaktiv-${g.slug}`}><span className="font-semibold">{g.name}</span> · inaktiv (Standort archiviert) – keine Anfragen oder Zuordnungen mehr</div>;
          return (
            <div key={g.slug} className={`rounded-2xl border p-6 transition-all ${isWarte ? "bg-purple-50 border-purple-200" : "bg-white border-slate-200"}`} data-testid={`gebiet-${g.slug}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${isWarte ? "bg-purple-200" : "bg-green-100"}`}>
                    <MapPin className={`h-5 w-5 ${isWarte ? "text-purple-700" : "text-green-700"}`} />
                  </div>
                  <div>
                    <h3 className="font-['Manrope'] text-lg font-bold text-slate-900">{g.name}</h3>
                    <span className={`text-xs font-semibold ${isWarte ? "text-purple-700" : "text-green-700"}`}>
                      {isWarte ? "Warteliste" : "Verfügbar"}
                    </span>
                  </div>
                </div>
                <Button size="sm" onClick={() => toggleStatus(g.slug)} data-testid={`toggle-${g.slug}`}
                  className={`rounded-xl text-xs ${isWarte ? "bg-green-600 hover:bg-green-700 text-white" : "bg-purple-600 hover:bg-purple-700 text-white"}`}>
                  {isWarte ? "Auf Verfügbar setzen" : "Warteliste aktivieren"}
                </Button>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div className="bg-white/80 rounded-lg p-2 text-center border border-slate-100">
                  <p className="text-lg font-bold text-slate-800">{stats.total}</p>
                  <p className="text-[10px] text-slate-500">Anfragen</p>
                </div>
                <div className="bg-white/80 rounded-lg p-2 text-center border border-slate-100">
                  <p className="text-lg font-bold text-amber-600">{stats.neu}</p>
                  <p className="text-[10px] text-slate-500">Neue</p>
                </div>
                <div className="bg-white/80 rounded-lg p-2 text-center border border-slate-100">
                  <p className="text-lg font-bold text-purple-600">{stats.warteliste}</p>
                  <p className="text-[10px] text-slate-500">Warteliste</p>
                </div>
                <div className="bg-white/80 rounded-lg p-2 text-center border border-slate-100">
                  <p className="text-lg font-bold text-orange-600">{stats.fenster}</p>
                  <p className="text-[10px] text-slate-500">Fenster</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
